import { ConversationDraftScope } from '@provision/conversation-types';
import { renderRichTextDocumentToHtml, stableStringify } from '@provision/formatting-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Conversation,
  DraftAttachmentInput,
  useConversationMessageDraft,
  useUpsertConversationMessageDraft,
} from 'src/graphql';
import { isOptimisticDraftId } from 'src/graphql/entities/conversation-message-draft/hooks/utils';
import {
  areSchedulesEqual,
  cloneSchedule,
  createScheduleFromSource,
  DraftSchedule,
  EMPTY_SCHEDULE,
  isScheduleEmpty,
  mergeSchedules,
} from './conversation-draft-schedule';

import {
  DraftScope,
  makeDraftKey,
  markSessionDraftSynced,
  readSessionDraft,
  resolveDraftContent,
  writeSessionDraft,
} from './conversation-draft-storage';
import { normalizeOutgoingMessageHtml } from './InputField/normalize-outgoing-message-html';

type DraftRecord = ReturnType<typeof useConversationMessageDraft>['drafts'][number];

export type UpsertStamp = {
  key: string;
  content: string;
  isPrivate: boolean;
  scheduleHash: string;
};

export const DEBOUNCE_MS = 1000;

/** accept either `scope` or a boolean `scheduled` (alias). */
type UseConversationDraftParams =
  | {
      conversationId: string;
      conversationPartnerId: number;
      isPrivate: boolean;
      conversation: Conversation;
      scope?: DraftScope;
      scheduled?: never;
    }
  | {
      conversationId: string;
      conversationPartnerId: number;
      isPrivate: boolean;
      conversation: Conversation;
      scheduled: boolean;
      scope?: never;
    };

export function useConversationDraft(params: UseConversationDraftParams) {
  const { conversationId, conversationPartnerId, isPrivate, conversation } = params;

  let resolvedScope: DraftScope = ConversationDraftScope.Unscheduled;

  if ('scheduled' in params) {
    if (params.scheduled) resolvedScope = ConversationDraftScope.Scheduled;
  } else if (params.scope) {
    resolvedScope = params.scope;
  }

  const isScheduledScope = resolvedScope === ConversationDraftScope.Scheduled;

  const { allDrafts, isLoading: draftsLoading } = useConversationMessageDraft();
  const { upsertConversationMessageDraft } = useUpsertConversationMessageDraft();

  // once the server query has resolved, an absent draft is authoritative
  const serverLoaded = !draftsLoading;

  const key = makeDraftKey(conversationId, isPrivate, resolvedScope);

  const serverDraft: DraftRecord | undefined = useMemo(
    () =>
      allDrafts.find(
        (d) =>
          d.conversation?.id === conversationId &&
          d.isPrivate === isPrivate &&
          Boolean(d.isScheduled) === isScheduledScope,
      ),
    [allDrafts, conversationId, isPrivate, isScheduledScope],
  );

  const serverHasDraft = Boolean(serverDraft);
  const serverContent = serverDraft?.message ?? '';
  const serverTs = serverDraft
    ? Date.parse(serverDraft.updatedAt ?? serverDraft.createdAt ?? '') || 0
    : 0;

  const uiRef = useRef<string>('');
  const readyRef = useRef<boolean>(false);

  const clearingRef = useRef(false);
  const lastUpsertRef = useRef<UpsertStamp | null>(null);
  const pendingRef = useRef<Promise<unknown> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const keyRef = useRef(key);

  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      const session = readSessionDraft(key);
      uiRef.current =
        resolveDraftContent({
          serverContent,
          serverTs,
          serverHasDraft,
          serverLoaded,
          session,
        }) || '';
      readyRef.current = true;
      lastUpsertRef.current = null;
    }
  }, [key, serverContent, serverTs, serverHasDraft, serverLoaded]);

  const session = readSessionDraft(key);

  const value =
    (readyRef.current
      ? uiRef.current
      : resolveDraftContent({
          serverContent,
          serverTs,
          serverHasDraft,
          serverLoaded,
          session,
        })) || '';

  useEffect(() => {
    const isConfirmed =
      serverDraft &&
      !isOptimisticDraftId(serverDraft.id) &&
      readSessionDraft(key).content === serverContent;

    if (isConfirmed) {
      markSessionDraftSynced(key, serverContent);
    }
  }, [serverDraft, serverContent, key]);

  const cancelDebounce = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const serverSchedule = useMemo(
    () =>
      createScheduleFromSource(
        serverDraft
          ? {
              scheduledAt: serverDraft.scheduledAt ?? null,
              sideEffects: serverDraft.sideEffects ?? null,
              sendUnless: serverDraft.sendUnless ?? null,
              discardedAt: serverDraft.discardedAt ?? null,
            }
          : undefined,
      ),
    [serverDraft],
  );

  const serverScheduleHash = useMemo(() => stableStringify(serverSchedule), [serverSchedule]);

  const [draftSchedule, setDraftSchedule] = useState<DraftSchedule>(() =>
    cloneSchedule(serverSchedule),
  );
  const scheduleRef = useRef<DraftSchedule>(draftSchedule);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    scheduleRef.current = draftSchedule;
  }, [draftSchedule]);

  useEffect(() => {
    const next = cloneSchedule(serverSchedule);
    if (!areSchedulesEqual(scheduleRef.current, next)) {
      scheduleRef.current = next;
      setDraftSchedule(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverScheduleHash]);

  const shouldSkipUpsert = useCallback(
    ({
      body,
      pvt,
      schedule,
      opts,
    }: {
      body: string;
      pvt: boolean;
      schedule: DraftSchedule;
      opts?: { allowEmpty?: boolean };
    }): boolean => {
      if (clearingRef.current && !opts?.allowEmpty) return true;
      const nothingLocal = !body && !session.content && isScheduleEmpty(schedule);
      if (!opts?.allowEmpty && !serverDraft && nothingLocal) return true;

      const scheduleHash = stableStringify(schedule);

      if (
        !opts?.allowEmpty &&
        serverDraft &&
        body === serverContent &&
        pvt === serverDraft.isPrivate &&
        scheduleHash === serverScheduleHash
      ) {
        return true;
      }
      const last = lastUpsertRef.current;
      if (
        last &&
        last.key === key &&
        last.content === body &&
        last.isPrivate === pvt &&
        last.scheduleHash === scheduleHash
      ) {
        return true;
      }
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const runUpsert = useCallback(
    (
      explicit?: {
        content?: string;
        isPrivate?: boolean;
        schedule?: DraftSchedule | Partial<DraftSchedule>;
        attachments?: DraftAttachmentInput[];
      },
      opts?: { allowEmpty?: boolean },
    ): Promise<unknown> | void => {
      const body = explicit?.content ?? uiRef.current ?? '';
      const pvt = explicit?.isPrivate ?? isPrivate;

      let schedule = scheduleRef.current;
      if (explicit?.schedule) {
        schedule = mergeSchedules(scheduleRef.current, explicit.schedule);
      }

      if (shouldSkipUpsert({ body, pvt, schedule, opts })) {
        return;
      }

      const scheduleHash = stableStringify(schedule);

      if (!areSchedulesEqual(scheduleRef.current, schedule)) {
        scheduleRef.current = schedule;
        setDraftSchedule(schedule);
      }

      setIsSaving(true);

      const html = normalizeOutgoingMessageHtml(renderRichTextDocumentToHtml(body) ?? '');
      const payload = {
        conversationId,
        partnerId: conversationPartnerId,
        message: body,
        messageHtml: html && html.trim().length > 0 ? html : null,
        isPrivate: pvt,
        scheduledAt: schedule.scheduledAt,
        sideEffects: schedule.sideEffects
          ? {
              actions: schedule.sideEffects.actions.map((action) => ({
                type: action.type,
                value: action.value ?? null,
              })),
            }
          : null,
        sendUnless:
          schedule.sendUnless?.map((item) => ({
            type: item.type,
            value: item.value ?? null,
          })) ?? null,
        discardedAt: schedule.discardedAt,
        // Only present when (re)scheduling; omitted on autosaves so the backend keeps the
        // existing draft attachment rows instead of replacing them.
        attachments: explicit?.attachments,
        // the storage key already encodes the scope; sessions are isolated via `key`
      };

      const promise =
        upsertConversationMessageDraft(payload, conversation, undefined)?.then(() => {
          lastUpsertRef.current = { key, content: body, isPrivate: pvt, scheduleHash };
        }) ?? Promise.resolve();

      pendingRef.current = promise;
      void promise.finally(() => {
        setIsSaving(false);
      });

      return promise;
    },
    [
      conversation,
      conversationId,
      conversationPartnerId,
      isPrivate,
      key,
      setDraftSchedule,
      setIsSaving,
      shouldSkipUpsert,
      upsertConversationMessageDraft,
    ],
  );

  const update = useCallback(
    (next: string) => {
      if (clearingRef.current) return;
      uiRef.current = next ?? '';
      readyRef.current = true;
      writeSessionDraft(key, uiRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
      setIsSaving(true);
      timerRef.current = setTimeout(() => {
        const result = runUpsert({ content: uiRef.current, isPrivate }) ?? null;
        pendingRef.current = result;
        if (!result) {
          setIsSaving(false);
        }
      }, DEBOUNCE_MS);
    },
    [isPrivate, key, runUpsert, setIsSaving],
  );

  const persist = useCallback(
    (
      next?: string,
      scheduleOverride?: DraftSchedule | Partial<DraftSchedule>,
      attachments?: DraftAttachmentInput[],
    ) => {
      if (clearingRef.current) return;
      if (typeof next === 'string') {
        uiRef.current = next;
        readyRef.current = true;
        writeSessionDraft(key, uiRef.current);
      }
      cancelDebounce();
      setIsSaving(true);
      const result =
        runUpsert({
          content: uiRef.current,
          isPrivate,
          schedule: scheduleOverride,
          attachments,
        }) ?? null;
      pendingRef.current = result;
      if (!result) {
        setIsSaving(false);
      }
    },
    [cancelDebounce, isPrivate, key, runUpsert, setIsSaving],
  );

  const clear = useCallback(async () => {
    if (clearingRef.current) return;
    clearingRef.current = true;

    cancelDebounce();

    const inflight = pendingRef.current;
    if (inflight) {
      try {
        await inflight;
      } catch {
        /* ignore */
      }
    }

    uiRef.current = '';
    writeSessionDraft(key, '');
    readyRef.current = true;
    const emptySchedule = cloneSchedule(EMPTY_SCHEDULE);
    scheduleRef.current = emptySchedule;
    setDraftSchedule(emptySchedule);

    setIsSaving(true);
    const result =
      runUpsert({ content: '', isPrivate, schedule: emptySchedule }, { allowEmpty: true }) ??
      null;
    pendingRef.current = result;
    if (result) {
      try {
        await result;
      } catch {
        /* ignore */
      }
    } else {
      setIsSaving(false);
    }

    clearingRef.current = false;
  }, [isPrivate, key, runUpsert, setDraftSchedule, setIsSaving, cancelDebounce]);

  useEffect(() => () => cancelDebounce(), [cancelDebounce]);

  const updateDraftSchedule = useCallback(
    (
      input:
        | DraftSchedule
        | Partial<DraftSchedule>
        | ((prev: DraftSchedule) => DraftSchedule | Partial<DraftSchedule>),
      opts?: { allowEmpty?: boolean },
    ) => {
      const resolved = typeof input === 'function' ? input(scheduleRef.current) : input;
      const nextSchedule = mergeSchedules(scheduleRef.current, resolved ?? EMPTY_SCHEDULE);

      setIsSaving(true);
      const result =
        runUpsert({ schedule: nextSchedule }, { allowEmpty: opts?.allowEmpty ?? true }) ??
        null;

      pendingRef.current = result;

      if (!result) {
        if (!areSchedulesEqual(scheduleRef.current, nextSchedule)) {
          scheduleRef.current = nextSchedule;
          setDraftSchedule(nextSchedule);
        }
        setIsSaving(false);
        return Promise.resolve();
      }
      return result;
    },
    [runUpsert, setDraftSchedule, setIsSaving],
  );

  return {
    // stringified editor.getJSON() (includes mentions + macros in meta)
    draftContent: value,
    debounceUpdateDraft: update,
    flushUpdateDraft: persist,
    clearDraft: clear,
    draftSchedule,
    updateDraftSchedule,
    isDraftSaving: isSaving,
    serverDraft,
  };
}
